import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router';
import { User, Site, Installer } from '../../../types';
import FormFieldSet from './FormFieldSet';
import { getCookie } from '../../../utils';
import { getProject } from '../../energy/api';
import { CheckIcon } from '@heroicons/react/20/solid';

interface InstallerWithSites {
    installer: Installer;
    sites: Site[];
    selectedSiteIds: number[];
}

const InvitationSendFormPage: React.FC = () => {
    const { user } = useOutletContext<{ user: User }>();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [installerGroups, setInstallerGroups] = useState<InstallerWithSites[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const project = await getProject(projectId!);
                const sitesWithInstallers = (project.sites || []).filter(site => site.installer !== null);
                
                // Group sites by installer
                const installerMap = new Map<number, InstallerWithSites>();
                
                sitesWithInstallers.forEach(site => {
                    if (site.installer) {
                        if (!installerMap.has(site.installer.id)) {
                            installerMap.set(site.installer.id, {
                                installer: site.installer,
                                sites: [],
                                selectedSiteIds: []
                            });
                        }
                        installerMap.get(site.installer.id)!.sites.push(site);
                    }
                });
                
                setInstallerGroups(Array.from(installerMap.values()));
            } catch (error) {
                console.error('Failed to fetch project:', error);
                alert('Failed to load project sites. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchProject();
    }, [projectId]);

    const toggleSiteSelection = (installerId: number, siteId: number) => {
        setInstallerGroups(prev => prev.map(group => {
            if (group.installer.id === installerId) {
                const isSelected = group.selectedSiteIds.includes(siteId);
                return {
                    ...group,
                    selectedSiteIds: isSelected
                        ? group.selectedSiteIds.filter(id => id !== siteId)
                        : [...group.selectedSiteIds, siteId]
                };
            }
            return group;
        }));
    };

    const selectAllSites = (installerId: number) => {
        setInstallerGroups(prev => prev.map(group => {
            if (group.installer.id === installerId) {
                return {
                    ...group,
                    selectedSiteIds: group.sites.map(site => site.id)
                };
            }
            return group;
        }));
    };

    const unselectAllSites = (installerId: number) => {
        setInstallerGroups(prev => prev.map(group => {
            if (group.installer.id === installerId) {
                return {
                    ...group,
                    selectedSiteIds: []
                };
            }
            return group;
        }));
    };

    const hasSelectedSites = installerGroups.some(group => group.selectedSiteIds.length > 0);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Filter to only groups with selected sites
            const groupsWithSelections = installerGroups.filter(group => group.selectedSiteIds.length > 0);
            
            const promises = groupsWithSelections.map(group => {
                return fetch(`/api/site_connect_invitations/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCookie('csrftoken') || '',
                    },
                    body: JSON.stringify({
                        project: projectId,
                        site_ids: group.selectedSiteIds,
                        installer: group.installer.id
                    }),
                });
            });

            const results = await Promise.all(promises);
            const failedRequests = results.filter(response => !response.ok);
            
            if (failedRequests.length > 0) {
                const errorMessages = failedRequests.map(response => response.statusText).join('\n');
                throw new Error(`Failed to send ${failedRequests.length} invitation(s):\n${errorMessages}`);
            }

            alert(`Successfully sent ${groupsWithSelections.length} invitation(s)!`);
            navigate(`/dashboard/project/${projectId}`);
            navigate(0);
        } catch (error) {
            console.error(error);
            alert('Failed to send invitations. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="p-6 flex justify-center items-center h-full">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="p-6 flex flex-col gap-6 justify-start items-center h-full overflow-y-auto">
            <div className="text-center">
                <h1 className="text-xl font-bold">Send Site Registration Invitations</h1>
                <p className="text-sm">Select sites to send registration invitations to their assigned installers</p>
            </div>

            {installerGroups.length === 0 ? (
                <div className="w-full max-w-3xl p-6 text-center">
                    <p className="text-gray-500">No sites with installers registered in this project.</p>
                    <p className="text-sm text-gray-400 mt-2">
                        Assign installers to sites first before sending invitations.
                    </p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="w-full max-w-3xl">
                    <FormFieldSet className="flex flex-col gap-4">
                        {installerGroups.map((group) => (
                            <div key={group.installer.id} className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="font-semibold text-base">{group.installer.name}</h3>
                                        <p className="text-xs text-gray-600">{group.installer.email}</p>
                                    </div>
                                    <div className="flex gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => selectAllSites(group.installer.id)}
                                            className="text-neutral hover:underline"
                                            disabled={isSubmitting}
                                        >
                                            Select All
                                        </button>
                                        <span className="text-gray-400">|</span>
                                        <button
                                            type="button"
                                            onClick={() => unselectAllSites(group.installer.id)}
                                            className="text-neutral hover:underline"
                                            disabled={isSubmitting}
                                        >
                                            None
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {group.sites.map(site => {
                                        const isSelected = group.selectedSiteIds.includes(site.id);
                                        return (
                                            <button
                                                key={site.id}
                                                type="button"
                                                onClick={() => toggleSiteSelection(group.installer.id, site.id)}
                                                disabled={isSubmitting}
                                                className={`
                                                    badge badge-sm gap-1 py-2 px-2 transition-all
                                                    ${isSelected 
                                                        ? 'badge-neutral' 
                                                        : 'badge-outline badge-neutral cursor-pointer'
                                                    }
                                                `}
                                            >
                                                {isSelected && <CheckIcon className="w-3 h-3" />}
                                                <span>{site.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {group.selectedSiteIds.length > 0 && (
                                    <p className="text-xs text-gray-600">
                                        {group.selectedSiteIds.length} site(s) selected
                                    </p>
                                )}
                            </div>
                        ))}

                        <div className="flex gap-4 justify-between mt-4">
                            <button 
                                type="button" 
                                className="btn btn-ghost flex-1" 
                                onClick={() => navigate(`/dashboard/project/${projectId}`)} 
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-neutral flex-1" 
                                disabled={isSubmitting || !hasSelectedSites}
                            >
                                {isSubmitting && <span className="loading loading-spinner"></span>}
                                {isSubmitting ? 'Sending...' : 'Send Invitations'}
                            </button>
                        </div>
                    </FormFieldSet>
                </form>
            )}
        </div>
    );
};

export default InvitationSendFormPage;